import { state, ensureSettings } from './state.js';
import {
  fetchRemoteLots,
  fetchCreditBalance,
  refreshLotBids,
  refreshLotBidsBatch,
  refreshAuctioneerLogo,
  type ApiConfig,
} from './api.js';
import { computeVisibleLots } from './lotOperations.js';
import { getEffectiveCloseTime } from './lotOperations/status.js';
import { pollNotifications } from './notificationFeed.js';
import type { Lot } from '../types';
import type { formatCountdown } from './formatting';
import { formatCurrencyDisplay } from './formatting';
import { TIMEOUTS, BID_REFRESH_INTERVALS, POLL_INTERVALS } from '../../config/timeouts';
import { LIMITS } from '../../config/limits';
import { createLogger } from '../../shared/logger.js';
import tableCellStyles from './tableRenderer/tableCells.module.css';
import { buildMeatBadge } from './tableRenderer/meatBadge.js';
import { buildPriceReviewControls } from './rendering.js';
import { buildFavoriteCell } from './tableRenderer/favoriteCell.js';

const log = createLogger('portal:polling');

const DEBUG = false;
function isDebug(): boolean {
  // Allow forcing debug via window flag or localStorage
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window as any).__LOT_DEBUG === true) return true;
  } catch {
    /* noop */
  }
  try {
    if (localStorage.getItem('lotlingo.debug') === 'true') return true;
  } catch {
    /* noop */
  }
  return DEBUG;
}
function logDebug(...args: unknown[]): void {
  if (isDebug()) {
    log.info(...args);
  }
}

const CREDIT_POLL_INTERVAL_MS = TIMEOUTS.CREDIT_POLL_INTERVAL_MS;
const BID_REFRESH_TICK_MS = TIMEOUTS.BID_REFRESH_TICK_MS;
const BID_REFRESH_BATCH_LIMIT = LIMITS.BID_REFRESH_BATCH_LIMIT;
const LOGO_REFRESH_TICK_MS = TIMEOUTS.LOGO_REFRESH_TICK_MS;
const LOGO_REFRESH_BATCH_LIMIT = LIMITS.LOGO_REFRESH_BATCH_LIMIT;
const COUNTDOWN_UPDATE_MS = TIMEOUTS.COUNTDOWN_UPDATE_MS;
const FAST_POLL_INTERVAL_MS = 10_000;
const FAST_POLL_WINDOW_MS = 2 * 60 * 1000;

type TimerHandle = number | null;
type ConfigLike = ApiConfig | null | undefined;
type RenderFn = () => void;
type MapRemoteDocFn = (doc: unknown) => Lot | null | undefined;
type CountdownFormatter = (closeTs: number) => ReturnType<typeof formatCountdown>;

type CountdownIntervalMs = number;
type PollIntervalResult = { interval: number; tier: string };

let pollTimer: TimerHandle = null;
let creditPollTimer: TimerHandle = null;
let bidRefreshTimer: TimerHandle = null;
/** True while the chained bid-refresh loop is active (timer set or cycle running). */
let bidRefreshLoopActive = false;
/** Re-entrancy guard: a slow cycle must not overlap with the next tick. */
let bidRefreshCycleInFlight = false;
/** Set when the API doesn't expose the batch refresh endpoint (deploy skew). */
let bidRefreshBatchUnavailable = false;
let logoRefreshTimer: TimerHandle = null;
let countdownUpdateTimer: TimerHandle = null;
let activeCountdownIntervalMs: CountdownIntervalMs = COUNTDOWN_UPDATE_MS;
let lastPageLoadAt = Date.now();
let lastSubmissionAt: number | null = null;

/**
 * Track the server timestamp from the last successful poll.
 * Used for delta sync - subsequent polls send this as 'since' to get only changes.
 */
let lastServerTime: string | null = null;

/**
 * Reset delta sync state (e.g., when user changes or on full refresh needed)
 */
export function resetDeltaSyncState(): void {
  lastServerTime = null;
  consecutiveEmptyRemotePolls = 0;
}

function markPageLoad(timestamp = Date.now()): void {
  lastPageLoadAt = timestamp;
}

export function markSubmissionActivity(timestamp = Date.now()): void {
  lastSubmissionAt = timestamp;
}

function hasLoadedLots(): boolean {
  return Array.isArray(state.lots) && state.lots.length > 0;
}
const lastBidRefreshAt: Record<string, number> = Object.create(null);
const postCloseRefreshCompleted: Record<string, boolean> = Object.create(null);
const logoRefreshAttempted: Record<string, boolean> = Object.create(null);
// Track which lead-time notifications have been sent per lot
const notifiedLeadLevels: Record<string, Set<number>> = Object.create(null);
// Track consecutive empty remote poll responses so we don't immediately
// wipe a previously populated lot list due to a transient empty fetch.
// After a small threshold we will accept that lots are truly empty.
let consecutiveEmptyRemotePolls = 0;

function isDocumentHidden(): boolean {
  return typeof document.visibilityState === 'string' && document.visibilityState === 'hidden';
}

function isLotPresent(lot: Lot | null | undefined): lot is Lot {
  return Boolean(lot);
}

function getVisibleLots(): Lot[] {
  const lots = computeVisibleLots();
  if (Array.isArray(lots)) {
    return lots as Lot[];
  }
  return [];
}

/**
 * Update an auctioneer logo image in-place without triggering a full re-render.
 * This prevents screen flicker when logos are refreshed.
 * Returns true if the in-place update was successful.
 * Exported for use by manual logo refresh handlers.
 */
export function updateLogoInPlace(lotId: string, newLogoUrl: string | undefined): boolean {
  if (!lotId || !newLogoUrl) return false;

  let updated = false;

  // Find all auctioneer logo images that might be associated with this lot
  try {
    // For card view - find card with matching lot id
    const cardLogo = document.querySelector(`[data-id="${lotId}"] .auctioneer-logo`);
    if (cardLogo && cardLogo instanceof HTMLImageElement && cardLogo.src !== newLogoUrl) {
      cardLogo.src = newLogoUrl;
      updated = true;
    }

    // For table view - find row by thumbnail's data-lot-id, then update logo in same row
    const thumbnail = document.querySelector(`img.image-thumbnail[data-lot-id="${lotId}"]`);
    if (thumbnail) {
      const row = thumbnail.closest('tr');
      if (row) {
        const logoImg = row.querySelector('.auctioneer-logo');
        if (logoImg && logoImg instanceof HTMLImageElement && logoImg.src !== newLogoUrl) {
          logoImg.src = newLogoUrl;
          updated = true;
        } else if (!logoImg) {
          // No logo image exists yet (was showing text fallback) - need full render
          return false;
        }
      }
    }
  } catch {
    // DOM query failed, will fall back to full render
    return false;
  }

  return updated;
}

/**
 * Update bid-related values in-place for a specific lot row.
 * Returns true if in-place update was successful.
 * Exported for use by manual bid refresh handlers.
 */
export function updateBidInPlace(lotId: string, lot: Lot): boolean {
  if (!lotId) return false;

  try {
    // Find the bid refresh button for this lot by data-id
    const bidButton = document.querySelector(
      `button[data-action="refresh-bid"][data-id="${lotId}"]`
    );
    if (!bidButton) {
      // Could be in card view - try finding by thumbnail's data-lot-id
      const thumbnail = document.querySelector(`img.image-thumbnail[data-lot-id="${lotId}"]`);
      if (!thumbnail) return false;

      // For card view, find the card container and update bid display
      const card = thumbnail.closest('.card, .lot-card');
      if (card) {
        // Card view bid update - find elements with bid data
        const bidLines = card.querySelectorAll('[class*="bidLine"]');
        if (bidLines.length >= 2) {
          const currentBidText = formatBidValue(lot.currentBid, lot.currentBidRaw);
          const nextBidText = formatBidValue(lot.nextRequiredBid, lot.nextRequiredBidRaw);
          bidLines[0].textContent = 'Current: ' + currentBidText;
          bidLines[1].textContent = 'Next: ' + nextBidText;
          return true;
        }
      }
      return false;
    }

    // Table view - find bid lines within the button
    const bidLinesContainer = bidButton.querySelector('.' + tableCellStyles.bidLines);
    if (!bidLinesContainer) return false;

    const bidLines = bidLinesContainer.querySelectorAll('.' + tableCellStyles.bidLine);
    if (bidLines.length < 2) return false;

    // Format bid values
    const currentBidText = formatBidValue(lot.currentBid, lot.currentBidRaw);
    const nextBidText = formatBidValue(lot.nextRequiredBid, lot.nextRequiredBidRaw);

    // Update the text content directly
    bidLines[0].textContent = 'Current: ' + currentBidText;
    bidLines[1].textContent = 'Next: ' + nextBidText;

    return true;
  } catch {
    // DOM query failed
  }
  return false;
}

/**
 * Update the meat badge / estimate cell in-place for a specific lot.
 * This prevents image flicker when estimates are edited.
 * Returns true if in-place update was successful.
 * Exported for use by estimate edit handlers.
 */
export function updateEstimateCellInPlace(lotId: string, lot: Lot): boolean {
  if (!lotId || !lot) return false;

  try {
    const settings = ensureSettings();

    // Find the table row by looking for the thumbnail with this lot ID
    const thumbnail = document.querySelector(`img.image-thumbnail[data-lot-id="${lotId}"]`);
    if (!thumbnail) {
      // Try finding by meat badge data-id
      const meatBadge = document.querySelector(`[data-action="edit-estimate"][data-id="${lotId}"]`);
      if (!meatBadge) return false;

      // Found the meat badge, replace its parent td content
      const cell = meatBadge.closest('td');
      if (cell) {
        const newHtml = buildMeatBadge(lot, settings);
        cell.innerHTML = newHtml;
        return true;
      }
      return false;
    }

    const row = thumbnail.closest('tr');
    if (!row) return false;

    // Find the td that contains the meat badge
    // It's the cell with data-action="edit-estimate"
    const meatBadgeCell = row.querySelector('td:has([data-action="edit-estimate"])');
    if (meatBadgeCell) {
      const newHtml = buildMeatBadge(lot, settings);
      meatBadgeCell.innerHTML = newHtml;
      return true;
    }

    // Fallback: find by class pattern
    const cells = row.querySelectorAll('td');
    for (const cell of cells) {
      const badge = cell.querySelector('[data-action="edit-estimate"]');
      if (badge) {
        const newHtml = buildMeatBadge(lot, settings);
        cell.innerHTML = newHtml;
        return true;
      }
    }

    return false;
  } catch {
    // DOM query failed
  }
  return false;
}

/**
 * Update the price review controls cell in-place for a specific lot.
 * This prevents image flicker when approve/reject/price-check is clicked.
 * Returns true if in-place update was successful.
 * Exported for use by price review handlers.
 */
export function updatePriceStatusInPlace(lotId: string, lot: Lot): boolean {
  if (!lotId || !lot) return false;

  try {
    // Find the table row by looking for the thumbnail with this lot ID
    const thumbnail = document.querySelector(`img.image-thumbnail[data-lot-id="${lotId}"]`);
    if (!thumbnail) {
      // Try finding by price review button data-id
      const priceButton = document.querySelector(
        `.price-review-button[data-id="${lotId}"], .price-check-button[data-id="${lotId}"]`
      );
      if (priceButton) {
        // Found the button, replace its parent cell content
        const cell = priceButton.closest('.price-status-cell');
        if (cell) {
          const SPINNER_TEMPLATE = '<span class="spinner" aria-hidden="true"></span>';
          const newHtml = buildPriceReviewControls(lot, state, SPINNER_TEMPLATE);
          cell.innerHTML = newHtml;
          return true;
        }
      }

      // Try finding by closed lot button
      const closedButton = document.querySelector(
        `.price-check-button--closed[data-id="${lotId}"]`
      );
      if (closedButton) {
        const cell = closedButton.closest('.price-status-cell');
        if (cell) {
          const SPINNER_TEMPLATE = '<span class="spinner" aria-hidden="true"></span>';
          const newHtml = buildPriceReviewControls(lot, state, SPINNER_TEMPLATE);
          cell.innerHTML = newHtml;
          return true;
        }
      }

      // Try finding by the price-review-controls wrapper div with data-lot-id
      // This works even when a spinner is showing (no buttons) for lots without images
      const controlsWrapper = document.querySelector(
        `.price-review-controls[data-lot-id="${lotId}"]`
      );
      if (controlsWrapper) {
        const cell = controlsWrapper.closest('.price-status-cell');
        if (cell) {
          const SPINNER_TEMPLATE = '<span class="spinner" aria-hidden="true"></span>';
          const newHtml = buildPriceReviewControls(lot, state, SPINNER_TEMPLATE);
          cell.innerHTML = newHtml;
          return true;
        }
      }

      // Element not found - can't do in-place update
      return false;
    }

    const row = thumbnail.closest('tr');
    if (!row) return false;

    // Find the price-status-cell
    const priceCell = row.querySelector('.price-status-cell');
    if (priceCell) {
      const SPINNER_TEMPLATE = '<span class="spinner" aria-hidden="true"></span>';
      const newHtml = buildPriceReviewControls(lot, state, SPINNER_TEMPLATE);
      priceCell.innerHTML = newHtml;
      return true;
    }

    return false;
  } catch {
    // DOM query failed
  }
  return false;
}

/**
 * Update the favorite cell in-place for a specific lot.
 * This prevents image flicker when toggling favorites.
 * Returns true if in-place update was successful.
 * Exported for use by favorite toggle handlers.
 */
export function updateFavoriteCellInPlace(lotId: string, lot: Lot): boolean {
  if (!lotId || !lot) return false;

  try {
    // Find the table row by looking for the thumbnail with this lot ID
    const thumbnail = document.querySelector(`img.image-thumbnail[data-lot-id="${lotId}"]`);
    if (!thumbnail) {
      // Try finding by favorite button data-id
      const favButton = document.querySelector(
        `button[data-action="favorite"][data-id="${lotId}"]`
      );
      if (!favButton) return false;

      // Found the button, replace the parent favorite-actions div content
      const actionsDiv = favButton.closest('.favorite-cell');
      if (actionsDiv) {
        // Check if lot is deleted
        const isDeleted = lot.aiStatus === 'deleted' || lot.status === 'deleted';
        const editIconButton = ''; // Edit button is built separately, preserve existing
        const newHtml = buildFavoriteCell(lot, state, isDeleted, editIconButton);
        actionsDiv.innerHTML = newHtml;
        return true;
      }
      return false;
    }

    const row = thumbnail.closest('tr');
    if (!row) return false;

    // Find the favorite-cell
    const favoriteCell = row.querySelector('.favorite-cell');
    if (favoriteCell) {
      // Check if lot is deleted
      const isDeleted = lot.aiStatus === 'deleted' || lot.status === 'deleted';
      const editIconButton = ''; // Edit button is built separately, preserve existing
      const newHtml = buildFavoriteCell(lot, state, isDeleted, editIconButton);
      favoriteCell.innerHTML = newHtml;
      return true;
    }

    return false;
  } catch {
    // DOM query failed
  }
  return false;
}

/**
 * Format a bid value for display
 */
function formatBidValue(
  numericValue: number | null | undefined,
  rawValue: string | null | undefined
): string {
  if (numericValue != null) {
    const formatted = formatCurrencyDisplay(Number(numericValue));
    return formatted != null ? formatted : '$' + String(numericValue);
  } else if (rawValue != null) {
    return String(rawValue);
  }
  return '-';
}

type SharedLotLike = {
  payload?: {
    raw?: unknown;
  };
};

function getBackendClosedFlag(lot: Lot): boolean | undefined {
  const sharedLot = (lot as unknown as { sharedLot?: SharedLotLike }).sharedLot;
  const raw = sharedLot?.payload?.raw;
  if (!raw || typeof raw !== 'object') return undefined;

  const lotlingo = (raw as Record<string, unknown>).lotlingo;
  if (!lotlingo || typeof lotlingo !== 'object') return undefined;

  const closed = (lotlingo as Record<string, unknown>).closed;
  return typeof closed === 'boolean' ? closed : undefined;
}

/**
 * Calculate appropriate refresh interval based on lot close time
 * Implements time-based refresh strategy:
 * - >24h until close: refresh every 6 hours
 * - <24h until close: refresh every hour
 * - <1h until close: refresh every 5 minutes
 * - <30min until close: refresh every 5 seconds
 * - After close: one final refresh within the 5min post-close window, then never
 */
function calculateRefreshInterval(lot: Lot, now: number): number {
  const closeTime = getEffectiveCloseTime(lot);
  if (!closeTime || closeTime === 0) {
    return BID_REFRESH_INTERVALS.DEFAULT;
  }

  const backendClosed = getBackendClosedFlag(lot);
  let timeUntilClose = closeTime - now;

  // If backend explicitly says the lot is NOT closed yet, do not treat
  // an elapsed close timestamp as final. The source site can extend close.
  if (timeUntilClose < 0 && backendClosed === false) {
    timeUntilClose = 0;
  }

  // Already closed - check if within 5-minute post-close window
  if (timeUntilClose < 0) {
    const timeSinceClose = Math.abs(timeUntilClose);
    if (timeSinceClose <= BID_REFRESH_INTERVALS.POST_CLOSE_WINDOW) {
      // Within 5min window - allow one final refresh
      return BID_REFRESH_INTERVALS.POST_CLOSE_WINDOW;
    }
    // Beyond 5min post-close - don't refresh
    return Infinity;
  }

  // Active lot - determine interval based on time remaining
  if (timeUntilClose < 30 * 60 * 1000) {
    return BID_REFRESH_INTERVALS.WITHIN_30MIN; // <30min: refresh every 5 seconds
  } else if (timeUntilClose < 60 * 60 * 1000) {
    return BID_REFRESH_INTERVALS.WITHIN_1H; // <1h: refresh every 5 minutes
  } else if (timeUntilClose < 24 * 60 * 60 * 1000) {
    return BID_REFRESH_INTERVALS.WITHIN_24H; // <24h: refresh every hour
  }

  return BID_REFRESH_INTERVALS.DEFAULT; // >24h: refresh every 6 hours
}

/**
 * Check if lot needs bid refresh based on last refresh time and close time
 * Only refresh if:
 * - Lot is not deleted
 * - Lot is synced to remote (has remote flag)
 * - Sufficient time has passed since last refresh (based on close time)
 * - For post-close lots, hasn't already completed the final refresh
 */
function shouldRefreshLotBid(lot: Lot, now: number): boolean {
  if (!lot.id || lot.deleted || lot.status === 'deleted') return false;
  if (!lot.remote) return false; // Only refresh synced lots
  if (lot.source !== 'kbid' && lot.source !== 'hibid') return false;
  if (!lot.url) return false;

  const lastRefresh = lastBidRefreshAt[lot.id] || 0;
  const timeSinceRefresh = now - lastRefresh;
  const requiredInterval = calculateRefreshInterval(lot, now);

  // If required interval is Infinity (old closed lot), never refresh
  if (!Number.isFinite(requiredInterval)) return false;

  // Check if lot is in post-close window
  const closeTime = getEffectiveCloseTime(lot);
  const backendClosed = getBackendClosedFlag(lot);
  if (closeTime && closeTime > 0 && now > closeTime) {
    // If backend indicates the lot is still open, allow continued refresh
    // even if the close timestamp has elapsed (close extensions).
    if (backendClosed === false) {
      return timeSinceRefresh >= requiredInterval;
    }
    // Lot is closed - only refresh once in the post-close window
    if (postCloseRefreshCompleted[lot.id]) return false;
  }

  return timeSinceRefresh >= requiredInterval;
}

/**
 * Calculate the optimal poll interval based on the soonest-closing visible lot.
 * This reduces RU costs by polling less frequently when all lots are stable.
 *
 * Tiers:
 * - First 2 minutes after page load or submission: 10s (fast start to catch fresh data)
 * - <30min until close: 10s (urgent - active bidding)
 * - <1h until close: 15s (active)
 * - <24h until close: 30s (soon)
 * - >24h until close: 30s (stable)
 * - no lots: 10s (check for new submissions)
 */
function computeOptimalPollInterval(): PollIntervalResult {
  const now = Date.now();
  const mostRecentStart = Math.max(lastPageLoadAt, lastSubmissionAt ?? 0);

  if (mostRecentStart && now - mostRecentStart <= FAST_POLL_WINDOW_MS) {
    return {
      interval: FAST_POLL_INTERVAL_MS,
      tier: 'fast-start (recent load/submission)',
    };
  }

  const visible = getVisibleLots();

  if (visible.length === 0) {
    return { interval: POLL_INTERVALS.EMPTY, tier: 'empty (no lots)' };
  }

  let soonestCloseMs = Infinity;

  for (const lot of visible) {
    if (!lot) continue;
    // Skip closed/deleted lots
    if (lot.deleted || lot.status === 'deleted') continue;

    const closeTime = getEffectiveCloseTime(lot);
    if (!closeTime || closeTime <= 0) continue;

    const timeUntilClose = closeTime - now;
    if (timeUntilClose > 0 && timeUntilClose < soonestCloseMs) {
      soonestCloseMs = timeUntilClose;
    }
  }

  // If no active lots with close times, use stable interval
  if (!Number.isFinite(soonestCloseMs)) {
    return { interval: POLL_INTERVALS.STABLE, tier: 'stable (>24h or unknown close)' };
  }

  // Determine tier based on soonest close time
  if (soonestCloseMs < 30 * 60 * 1000) {
    return { interval: POLL_INTERVALS.URGENT, tier: 'urgent (<30min)' };
  } else if (soonestCloseMs < 60 * 60 * 1000) {
    return { interval: POLL_INTERVALS.ACTIVE, tier: 'active (<1h)' };
  } else if (soonestCloseMs < 24 * 60 * 60 * 1000) {
    return { interval: POLL_INTERVALS.SOON, tier: 'soon (<24h)' };
  }

  return { interval: POLL_INTERVALS.STABLE, tier: 'stable (>24h)' };
}

const DEFAULT_POLL_TIER = 'stable (>24h)';
/** Track current poll interval for dynamic adjustment */
let currentPollIntervalMs: number = POLL_INTERVALS.STABLE;
let currentPollTier: string = DEFAULT_POLL_TIER;

function upsertLot(mapped: Lot) {
  const lots = state.lots as Lot[];
  const idx = lots.findIndex(candidate => candidate && candidate.id === mapped.id);
  if (idx >= 0) {
    lots[idx] = mapped;
  } else {
    lots.unshift(mapped);
  }
}

/**
 * Generate a fingerprint of lot data that we care about for change detection.
 * Only includes fields that affect the visual display.
 */
function getLotFingerprint(lot: Lot): string {
  return JSON.stringify({
    id: lot.id,
    title: lot.title,
    currentBid: lot.currentBid,
    currentBidRaw: lot.currentBidRaw,
    nextRequiredBid: lot.nextRequiredBid,
    nextRequiredBidRaw: lot.nextRequiredBidRaw,
    closeAt: lot.closeAt,
    closeAtRaw: lot.closeAtRaw,
    estimateLow: lot.estimateLow,
    estimateHigh: lot.estimateHigh,
    auctioneerLogoUrl: lot.auctioneerLogoUrl,
    favorite: lot.favorite,
    deleted: lot.deleted,
  });
}

/**
 * Check if the lots have materially changed.
 * Returns true if a full render is needed.
 */
function hasLotsChanged(prevLots: Lot[], newLots: Lot[]): boolean {
  if (prevLots.length !== newLots.length) return true;

  // Create a map of previous lot fingerprints
  const prevMap = new Map<string, string>();
  for (const lot of prevLots) {
    if (lot && lot.id) {
      prevMap.set(lot.id, getLotFingerprint(lot));
    }
  }

  // Check if any new lot differs from its previous version
  for (const lot of newLots) {
    if (!lot || !lot.id) continue;
    const prevFingerprint = prevMap.get(lot.id);
    if (!prevFingerprint) return true; // New lot added
    if (prevFingerprint !== getLotFingerprint(lot)) return true; // Lot changed
  }

  return false;
}

/**
 * Merge delta (changed) lots into existing lot list.
 * Updates existing lots in-place and adds new ones.
 */
function mergeDeltaLots(existingLots: Lot[], deltaLots: Lot[]): Lot[] {
  if (deltaLots.length === 0) return existingLots;

  // Create a map of existing lots by ID for fast lookup
  const lotMap = new Map<string, Lot>();
  for (const lot of existingLots) {
    if (lot && lot.id) {
      lotMap.set(lot.id, lot);
    }
  }

  // Merge delta lots - update existing or add new
  for (const deltaLot of deltaLots) {
    if (deltaLot && deltaLot.id) {
      lotMap.set(deltaLot.id, deltaLot);
    }
  }

  // Convert back to array, preserving original order for existing lots
  // and appending new lots at the end
  const result: Lot[] = [];
  const processedIds = new Set<string>();

  // First, maintain order of existing lots (updating with delta if present)
  for (const lot of existingLots) {
    if (lot && lot.id) {
      const updated = lotMap.get(lot.id);
      if (updated) {
        result.push(updated);
        processedIds.add(lot.id);
      }
    }
  }

  // Then, add any truly new lots from delta
  for (const deltaLot of deltaLots) {
    if (deltaLot && deltaLot.id && !processedIds.has(deltaLot.id)) {
      result.unshift(deltaLot); // New lots at the top
    }
  }

  return result;
}

async function fetchRemoteLotsAndUpdate(
  cfg: ConfigLike,
  mapRemoteDocFn: MapRemoteDocFn,
  renderFn: RenderFn
): Promise<void> {
  const startedAt = Date.now();
  // Pass impersonatedUserId so polling respects superadmin user selection
  const effectiveUserId = state.impersonatedUserId ?? null;

  // Use delta sync if we have a previous server timestamp and existing lots
  const useDeltaSync = Boolean(lastServerTime && hasLoadedLots());

  const result = await fetchRemoteLots(cfg ?? null, {
    onlyUserId: effectiveUserId,
    since: useDeltaSync ? (lastServerTime ?? undefined) : undefined,
  });

  const durationMs = Date.now() - startedAt;
  if (!result.success) {
    logDebug('remote poll failed', { error: result.error, durationMs });
    // On failure, reset delta sync so next poll is a full fetch
    if (useDeltaSync) {
      lastServerTime = null;
    }
    return;
  }
  if (!Array.isArray(result.data)) {
    logDebug('remote poll returned non-array data; ignoring');
    return;
  }

  // Update server time for next delta sync
  if (result.serverTime) {
    lastServerTime = result.serverTime;
  }

  const mapped = result.data.map(mapRemoteDocFn).filter(isLotPresent);
  const prevCount = Array.isArray(state.lots) ? state.lots.length : 0;
  const incomingCount = mapped.length;
  const isDelta = result.isDelta ?? useDeltaSync;

  // For delta sync: empty response means no changes, not that lots were deleted
  if (isDelta && incomingCount === 0) {
    logDebug('remote poll: delta returned 0 changes (no updates)', { durationMs });
    consecutiveEmptyRemotePolls = 0; // Reset - this is expected for delta
    return;
  }

  // For full sync: handle consecutive empty responses
  const willPreserve =
    !isDelta && incomingCount === 0 && prevCount > 0 && consecutiveEmptyRemotePolls < 9;
  const willClear =
    !isDelta && incomingCount === 0 && prevCount > 0 && consecutiveEmptyRemotePolls >= 9;

  if (!isDelta && incomingCount === 0 && prevCount > 0) {
    consecutiveEmptyRemotePolls += 1;
  } else if (incomingCount > 0) {
    consecutiveEmptyRemotePolls = 0;
  }

  const action = isDelta
    ? 'merge-delta'
    : willPreserve
      ? 'preserve-existing'
      : willClear
        ? 'clear-to-empty'
        : 'replace-with-incoming';

  logDebug('remote poll', {
    durationMs,
    prevCount,
    incomingCount,
    isDelta,
    consecutiveEmptyRemotePolls,
    impersonatedUserId: effectiveUserId,
    action,
  });

  // Expose quick inspection data on window for live debugging
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__LL_POLL = {
      prevCount,
      incomingCount,
      isDelta,
      lastServerTime,
      consecutiveEmptyRemotePolls,
      action,
      lastDurationMs: durationMs,
      lastUpdatedAt: Date.now(),
    };
  } catch {
    /* noop */
  }

  if (willPreserve) {
    return; // keep existing list this cycle
  }

  // For delta sync: merge changes into existing lots
  // For full sync: replace entire lot list
  const prevLots = (state.lots || []) as Lot[];
  let newLots: Lot[];
  let dataChanged: boolean;

  if (isDelta) {
    newLots = mergeDeltaLots(prevLots, mapped);
    // For delta, we know data changed if we received any items
    dataChanged = incomingCount > 0;
  } else {
    newLots = mapped;
    dataChanged = hasLotsChanged(prevLots, newLots);
  }

  // Accept clearing after threshold or replacing with non-empty
  state.lots = newLots;

  // Only re-render if the data actually changed
  if (dataChanged) {
    // For delta syncs, try in-place updates for each changed lot to avoid image flicker
    if (isDelta && incomingCount > 0) {
      logDebug('remote poll: delta - attempting in-place updates', { count: incomingCount });
      let allUpdatesSucceeded = true;
      for (const lot of mapped) {
        if (lot && lot.id) {
          // Try to update both price status and estimate cells in-place
          const priceUpdated = updatePriceStatusInPlace(lot.id, lot);
          const estimateUpdated = updateEstimateCellInPlace(lot.id, lot);
          const bidUpdated = updateBidInPlace(lot.id, lot);
          // If none of the in-place updates worked, we need a full render
          if (!priceUpdated && !estimateUpdated && !bidUpdated) {
            logDebug('remote poll: in-place update failed for lot, falling back to render', {
              lotId: lot.id,
            });
            allUpdatesSucceeded = false;
            break;
          }
        }
      }
      if (!allUpdatesSucceeded) {
        logDebug('remote poll: falling back to full render');
        renderFn();
      } else {
        logDebug('remote poll: all in-place updates succeeded, no full render needed');
      }
    } else {
      // For full syncs or non-delta changes, do a full render
      logDebug('remote poll: data changed, rendering');
      renderFn();
    }
  } else {
    logDebug('remote poll: no changes, skipping render');
  }
}

export function schedulePolling(
  cfg: ConfigLike,
  mapRemoteDocFn: MapRemoteDocFn,
  renderFn: RenderFn
) {
  if (state.mode !== 'auth0' || isDocumentHidden()) {
    cancelPolling();
    return;
  }
  if (pollTimer) {
    return;
  }
  markPageLoad();
  // Expose state reference for external inspection
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__LL_STATE = state;
  } catch {
    /* noop */
  }

  // Trigger immediate fetch before starting the adaptive polling loop
  // This ensures lots load instantly instead of waiting for the first interval tick
  void fetchRemoteLotsAndUpdate(cfg, mapRemoteDocFn, renderFn);

  // Start adaptive polling loop with setTimeout (allows interval adjustment per tick)
  scheduleNextPoll(cfg, mapRemoteDocFn, renderFn);
}

export async function triggerImmediatePoll(
  cfg: ConfigLike,
  mapRemoteDocFn: MapRemoteDocFn,
  renderFn: RenderFn,
  reason: 'submission' | 'manual' = 'manual'
): Promise<void> {
  if (state.mode !== 'auth0' || isDocumentHidden()) {
    return;
  }

  if (reason === 'submission') {
    markSubmissionActivity();
  }

  if (pollTimer) {
    window.clearTimeout(pollTimer);
    pollTimer = null;
  }

  resetDeltaSyncState();
  await fetchRemoteLotsAndUpdate(cfg, mapRemoteDocFn, renderFn);
  scheduleNextPoll(cfg, mapRemoteDocFn, renderFn);
}

/**
 * Schedule the next poll with an adaptive interval based on lot urgency.
 * Uses setTimeout instead of setInterval so we can adjust timing each cycle.
 */
function scheduleNextPoll(cfg: ConfigLike, mapRemoteDocFn: MapRemoteDocFn, renderFn: RenderFn) {
  if (state.mode !== 'auth0' || isDocumentHidden()) {
    cancelPolling();
    return;
  }

  // Calculate optimal interval based on current lot state
  const { interval: optimalInterval, tier } = computeOptimalPollInterval();

  // Log when interval changes (for debugging RU savings)
  if (optimalInterval !== currentPollIntervalMs || tier !== currentPollTier) {
    logDebug('poll interval adjusted', {
      from: currentPollIntervalMs,
      to: optimalInterval,
      tier,
    });
    currentPollIntervalMs = optimalInterval;
    currentPollTier = tier;
  }

  // Expose poll stats for live debugging
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__LL_POLL_INTERVAL = {
      currentMs: currentPollIntervalMs,
      nextPollAt: Date.now() + currentPollIntervalMs,
      tier: currentPollTier,
    };
  } catch {
    /* noop */
  }

  pollTimer = window.setTimeout(async () => {
    if (state.mode === 'auth0' && !isDocumentHidden()) {
      await fetchRemoteLotsAndUpdate(cfg, mapRemoteDocFn, renderFn);
      // Schedule next poll after this one completes
      scheduleNextPoll(cfg, mapRemoteDocFn, renderFn);
    }
  }, currentPollIntervalMs);
}

export function cancelPolling() {
  if (pollTimer) {
    window.clearTimeout(pollTimer);
    pollTimer = null;
  }
  // Reset interval tracking on cancel so next start recalculates
  currentPollIntervalMs = POLL_INTERVALS.STABLE;
  currentPollTier = DEFAULT_POLL_TIER;
}

export function scheduleCreditPolling(cfg?: ConfigLike) {
  if (state.mode !== 'auth0' || isDocumentHidden()) {
    cancelCreditPolling();
    return;
  }
  if (creditPollTimer) {
    return;
  }
  void fetchCreditBalance(cfg ?? null, false);
  pollNotifications();
  creditPollTimer = window.setInterval(() => {
    if (state.mode === 'auth0' && !isDocumentHidden()) {
      void fetchCreditBalance(cfg ?? null, false);
      pollNotifications();
    }
  }, CREDIT_POLL_INTERVAL_MS);
}

export function cancelCreditPolling() {
  if (creditPollTimer) {
    window.clearInterval(creditPollTimer);
    creditPollTimer = null;
  }
}

/**
 * Refresh a set of lots, preferring the batch endpoint and falling back to
 * sequential per-lot requests when the API doesn't support batching yet.
 * Returns a map of lot id -> refreshed document. Per-lot failures are
 * logged and simply omitted from the map.
 */
async function fetchRefreshedBidDocs(
  cfg: ConfigLike,
  ids: string[]
): Promise<Map<string, unknown>> {
  const docs = new Map<string, unknown>();

  if (!bidRefreshBatchUnavailable && ids.length > 1) {
    try {
      const results = await refreshLotBidsBatch(cfg ?? null, ids, false);
      for (const entry of results) {
        if (entry && entry.ok && entry.id && entry.doc) {
          docs.set(entry.id, entry.doc);
        } else if (entry && !entry.ok) {
          logDebug(`[bid-refresh] Batch refresh failed for lot ${entry.id}:`, entry.error);
        }
      }
      return docs;
    } catch (err) {
      if (err instanceof Error && err.name === 'BatchUnsupportedError') {
        bidRefreshBatchUnavailable = true;
        logDebug('[bid-refresh] Batch endpoint unavailable; using per-lot requests');
      } else {
        logDebug('[bid-refresh] Batch refresh request failed:', err);
        return docs;
      }
    }
  }

  for (const id of ids) {
    try {
      const doc = await refreshLotBids(cfg ?? null, id, false);
      docs.set(id, doc);
    } catch (err) {
      logDebug(`[bid-refresh] Failed to refresh lot ${id}:`, err);
    }
  }
  return docs;
}

/**
 * Run one cycle of bid refresh for visible lots.
 * Only refreshes lots that meet the time-based criteria.
 * Portal must be visible for any automatic refresh to occur.
 * Re-entrancy guarded: a cycle that outlives the tick is never overlapped.
 * Batches all updates and renders only once at the end to prevent flicker.
 * Uses in-place DOM updates when possible to avoid destroying images.
 */
async function runBidRefreshCycleOnce(
  cfg: ConfigLike,
  mapRemoteDocFn: MapRemoteDocFn,
  renderFn: RenderFn
): Promise<void> {
  if (state.mode !== 'auth0' || isDocumentHidden() || !hasLoadedLots()) {
    return;
  }
  if (bidRefreshCycleInFlight) {
    logDebug('[bid-refresh] Previous cycle still in flight; skipping');
    return;
  }
  bidRefreshCycleInFlight = true;

  const now = Date.now();

  try {
    const visible = getVisibleLots();

    // Filter lots that need refresh based on time rules, then take the
    // least-recently-refreshed first so lots beyond the batch limit are
    // not starved when everything is eligible near close.
    const lotsToRefresh = visible
      .filter(lot => shouldRefreshLotBid(lot, now))
      .sort((a, b) => (lastBidRefreshAt[a.id] || 0) - (lastBidRefreshAt[b.id] || 0))
      .slice(0, BID_REFRESH_BATCH_LIMIT);

    if (lotsToRefresh.length === 0) {
      logDebug('[bid-refresh] No lots need refresh at this time');
      return;
    }

    logDebug(`[bid-refresh] Refreshing ${lotsToRefresh.length} lots`);

    const ids = lotsToRefresh.map(lot => lot.id).filter((id): id is string => Boolean(id));
    const docs = await fetchRefreshedBidDocs(cfg, ids);

    // Mark every attempted lot (success or failure) so persistently
    // failing lots back off to their tier interval instead of hot-looping
    // every tick.
    const completedAt = Date.now();
    for (const id of ids) {
      lastBidRefreshAt[id] = completedAt;
    }

    let refreshed = 0;
    let needsFullRender = false;
    for (const lot of lotsToRefresh) {
      const doc = docs.get(lot.id);
      if (!doc) continue;

      const mapped = mapRemoteDocFn(doc);
      if (mapped) {
        upsertLot(mapped);
        // Try in-place update - only need full render if this fails
        const inPlaceSuccess = updateBidInPlace(lot.id, mapped);
        if (!inPlaceSuccess) {
          needsFullRender = true;
        }
      }

      // Mark post-close refresh as completed if this lot is closed
      const closeTime = getEffectiveCloseTime(lot);
      if (closeTime && closeTime > 0 && Date.now() > closeTime) {
        postCloseRefreshCompleted[lot.id] = true;
      }

      refreshed += 1;
    }

    // Only render if in-place updates failed for any lot
    if (needsFullRender) {
      renderFn();
    }

    if (refreshed > 0) {
      logDebug(`[bid-refresh] Successfully refreshed ${refreshed} lots`);
    }
  } catch (err) {
    logDebug('[bid-refresh] Cycle failed:', err);
  } finally {
    bidRefreshCycleInFlight = false;
  }
}

/**
 * Chain the next bid-refresh tick. Uses setTimeout (not setInterval) so a
 * slow cycle delays the next tick instead of overlapping with it.
 */
function scheduleNextBidRefreshTick(
  cfg: ConfigLike,
  mapRemoteDocFn: MapRemoteDocFn,
  renderFn: RenderFn
) {
  if (!bidRefreshLoopActive || state.mode !== 'auth0' || isDocumentHidden()) {
    cancelBidRefreshPolling();
    return;
  }
  bidRefreshTimer = window.setTimeout(async () => {
    bidRefreshTimer = null;
    if (!bidRefreshLoopActive) {
      return;
    }
    if (state.mode === 'auth0' && !isDocumentHidden()) {
      await runBidRefreshCycleOnce(cfg, mapRemoteDocFn, renderFn);
      scheduleNextBidRefreshTick(cfg, mapRemoteDocFn, renderFn);
    } else {
      cancelBidRefreshPolling();
    }
  }, BID_REFRESH_TICK_MS);
}

export function scheduleBidRefresh(
  cfg?: ConfigLike,
  mapRemoteDocFn?: MapRemoteDocFn,
  renderFn?: RenderFn
) {
  if (state.mode !== 'auth0' || isDocumentHidden()) {
    cancelBidRefreshPolling();
    return;
  }
  if (!hasLoadedLots() || bidRefreshLoopActive) {
    return;
  }
  // Only schedule if we have the required functions
  if (!cfg || !mapRemoteDocFn || !renderFn) {
    return;
  }
  bidRefreshLoopActive = true;
  // Trigger an immediate refresh check so users don't have to wait for the
  // first tick, then chain ticks after each cycle completes.
  void (async () => {
    await runBidRefreshCycleOnce(cfg, mapRemoteDocFn, renderFn);
    scheduleNextBidRefreshTick(cfg, mapRemoteDocFn, renderFn);
  })();
}

export function cancelBidRefreshPolling() {
  bidRefreshLoopActive = false;
  if (bidRefreshTimer) {
    window.clearTimeout(bidRefreshTimer);
    bidRefreshTimer = null;
  }
}

async function runLogoRefreshCycleOnce(
  cfg: ConfigLike,
  mapRemoteDocFn: MapRemoteDocFn,
  renderFn: RenderFn
): Promise<void> {
  if (state.mode !== 'auth0' || isDocumentHidden()) {
    return;
  }
  try {
    const visible = getVisibleLots();
    let refreshed = 0;
    let needsFullRender = false;
    for (let i = 0; i < visible.length; i += 1) {
      const lot = visible[i];
      if (!lot || !lot.id) continue;
      if (lot.source !== 'kbid' && lot.source !== 'hibid') continue;
      if (!lot.auctioneerLink) continue;

      const hasValidLogo =
        lot.auctioneerLogoUrl &&
        lot.auctioneerLogoUrl !== 'https://cdn.hibid.com/cdn/images/logos/hibid//mainlogo.png';
      if (hasValidLogo || logoRefreshAttempted[lot.id]) continue;

      try {
        const doc = await refreshAuctioneerLogo(cfg ?? null, lot.id, false);
        const mapped = mapRemoteDocFn(doc);
        if (mapped) {
          upsertLot(mapped);
          // Try to update logo in-place to avoid full re-render flicker
          const updatedInPlace = updateLogoInPlace(lot.id, mapped.auctioneerLogoUrl);
          if (!updatedInPlace) {
            // Could not update in-place (logo element doesn't exist yet), need full render
            needsFullRender = true;
          }
        }
        logoRefreshAttempted[lot.id] = true;
        refreshed += 1;
        if (refreshed >= LOGO_REFRESH_BATCH_LIMIT) {
          break;
        }
      } catch (err) {
        logoRefreshAttempted[lot.id] = true;
        if (DEBUG) {
          log.warn('logo refresh failed for', { lotId: lot && lot.id, err });
        }
      }
    }
    // Only render if we couldn't do in-place updates (e.g., switching from text fallback to logo)
    if (needsFullRender) {
      renderFn();
    }
  } catch (err) {
    if (DEBUG) {
      log.warn('logo refresh cycle failed', err);
    }
  }
}

export function scheduleLogoRefresh(
  cfg?: ConfigLike,
  mapRemoteDocFn?: MapRemoteDocFn,
  renderFn?: RenderFn
) {
  if (state.mode !== 'auth0' || isDocumentHidden()) {
    cancelLogoRefreshPolling();
    return;
  }
  if (logoRefreshTimer) {
    return;
  }
  // Only schedule if we have the required functions
  if (!cfg || !mapRemoteDocFn || !renderFn) {
    return;
  }
  void runLogoRefreshCycleOnce(cfg, mapRemoteDocFn, renderFn);
  logoRefreshTimer = window.setInterval(() => {
    void runLogoRefreshCycleOnce(cfg, mapRemoteDocFn, renderFn);
  }, LOGO_REFRESH_TICK_MS);
}

export function cancelLogoRefreshPolling() {
  if (logoRefreshTimer) {
    window.clearInterval(logoRefreshTimer);
    logoRefreshTimer = null;
  }
}

export function updateCountdownDisplays(formatCountdownFn: CountdownFormatter) {
  const countdownCellSelector = '.' + tableCellStyles.countdownCell + '[data-close-ts]';
  const countdownTimeSelector = '.' + tableCellStyles.countdownTime;
  const countdownUrgentClass = tableCellStyles.countdownUrgent;
  const cells = document.querySelectorAll<HTMLElement>(countdownCellSelector);
  let hasUrgent = false;
  cells.forEach(cell => {
    const closeTs = Number(cell.getAttribute('data-close-ts'));
    if (!Number.isFinite(closeTs)) return;
    const countdown = formatCountdownFn(closeTs);
    const timeEl = cell.querySelector<HTMLElement>(countdownTimeSelector);
    if (timeEl && timeEl.textContent !== countdown.text) {
      timeEl.textContent = countdown.text;
    }
    if (countdown.isUrgent) {
      hasUrgent = true;
      if (!cell.classList.contains(countdownUrgentClass)) {
        cell.classList.add(countdownUrgentClass);
      }
    } else if (cell.classList.contains(countdownUrgentClass)) {
      cell.classList.remove(countdownUrgentClass);
    }
  });

  // After updating countdowns, attempt closing-soon notifications
  maybeNotifyClosingSoon();

  if (hasUrgent && countdownUpdateTimer && activeCountdownIntervalMs > 10_000) {
    cancelCountdownUpdates();
    scheduleCountdownUpdates(formatCountdownFn, 10_000);
  } else if (
    !hasUrgent &&
    countdownUpdateTimer &&
    activeCountdownIntervalMs !== COUNTDOWN_UPDATE_MS
  ) {
    cancelCountdownUpdates();
    scheduleCountdownUpdates(formatCountdownFn, COUNTDOWN_UPDATE_MS);
  }
}

export function scheduleCountdownUpdates(formatCountdownFn: CountdownFormatter, interval?: number) {
  if (countdownUpdateTimer) return;
  const ms = interval ?? COUNTDOWN_UPDATE_MS;
  activeCountdownIntervalMs = ms;
  countdownUpdateTimer = window.setInterval(() => {
    updateCountdownDisplays(formatCountdownFn);
  }, ms);
}

export function cancelCountdownUpdates() {
  if (countdownUpdateTimer) {
    window.clearInterval(countdownUpdateTimer);
    countdownUpdateTimer = null;
    activeCountdownIntervalMs = COUNTDOWN_UPDATE_MS;
  }
}

export function setupVisibilityHandler(
  cfg: ConfigLike,
  mapRemoteDocFn: MapRemoteDocFn,
  renderFn: RenderFn,
  formatCountdownFn: CountdownFormatter
) {
  document.addEventListener('visibilitychange', () => {
    if (isDocumentHidden()) {
      // Stop all polling when portal not visible
      cancelPolling();
      cancelCreditPolling();
      cancelBidRefreshPolling();
      cancelLogoRefreshPolling();
      cancelCountdownUpdates();
    } else {
      if (state.mode === 'auth0') {
        void fetchRemoteLotsAndUpdate(cfg, mapRemoteDocFn, renderFn);
      }
      // Resume all polling
      schedulePolling(cfg, mapRemoteDocFn, renderFn);
      scheduleCreditPolling(cfg);
      scheduleBidRefresh(cfg, mapRemoteDocFn, renderFn);
      scheduleLogoRefresh(cfg, mapRemoteDocFn, renderFn);
      scheduleCountdownUpdates(formatCountdownFn);
      // Trigger immediate bid refresh check when portal becomes visible
      if (state.mode === 'auth0' && hasLoadedLots()) {
        void runBidRefreshCycleOnce(cfg, mapRemoteDocFn, renderFn);
      }
      // Re-evaluate notifications on visibility change
      maybeNotifyClosingSoon();
    }
  });
}

/**
 * Attempt to send closing-soon notifications for lots at specified lead times.
 * Lead times (in minutes) come from settings.notifyLeadMinutes (default [60,30,10]).
 */
function maybeNotifyClosingSoon(): void {
  try {
    // Basic capability / permission checks
    if (typeof window === 'undefined' || typeof Notification === 'undefined') return;
    const settings = ensureSettings();
    if (!settings.enableNotifications) return;
    if (Notification.permission !== 'granted') return;

    const rawLeadMinutes = settings.notifyLeadMinutes;
    const leadMinutes: number[] = Array.isArray(rawLeadMinutes)
      ? rawLeadMinutes
          .filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
          .map(Number)
      : [60, 30, 10];

    const now = Date.now();
    const lots = state.lots || [];
    for (let i = 0; i < lots.length; i++) {
      const lot = lots[i] as Lot;
      if (!lot || !lot.id || lot.deleted) continue;
      const closeTs = getEffectiveCloseTime(lot);
      if (!closeTs || closeTs <= 0) continue;
      const diffMs = closeTs - now;
      if (diffMs <= 0) continue; // Already closed
      const minutesRemaining = Math.round(diffMs / 60000);
      if (minutesRemaining < 0) continue;

      for (let j = 0; j < leadMinutes.length; j++) {
        const lead = leadMinutes[j];
        if (minutesRemaining === lead) {
          const sentSet = (notifiedLeadLevels[lot.id] ||= new Set<number>());
          if (!sentSet.has(lead)) {
            // Fire notification
            const title = `Lot closing in ${lead}m`;
            const bodyParts: string[] = [];
            if (lot.title) bodyParts.push(lot.title);
            if (lot.nextRequiredBid != null) {
              bodyParts.push('Next bid: $' + String(lot.nextRequiredBid));
            }
            const body = bodyParts.join(' \u2014 ');
            try {
              new Notification(title, {
                body,
                tag: 'll-lot-' + lot.id + '-lead-' + lead,
              } as NotificationOptions);
            } catch {
              /* ignore permission errors */
            }
            sentSet.add(lead);
          }
        }
      }
    }
  } catch {
    /* noop */
  }
}
